/**
 * FeedbackManagement — v2 admin feedback triage view
 * (specs/open/feedback-submissions-management.md §9.2), rendered as the
 * "Feedback Management" tab inside AdminPanel (§9.1). No longer owns its
 * own overlay/close chrome -- AdminPanel does -- this component is just
 * the tab's content.
 *
 * Three parts, top to bottom: a sinceDays time-range control (7/14/30/60d,
 * default 7d), aggregate metrics (total/positive/negative) over the
 * selected window, and a negative-feedback-only detail list showing the
 * original question/answer/referencedFiles with a "File Tickety ticket"
 * action. Positive feedback is represented only by its count in the
 * aggregate metrics -- no per-item detail, no ticket action (§9.2's
 * deliberate asymmetric design; the backend contract is unchanged, this
 * narrowing is UI-only).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { fetchAdminFeedback, AdminFeedbackItem, VALID_SINCE_DAYS, SinceDays } from './fetchAdminFeedback';
import { createFeedbackTicket, CreateFeedbackTicketError } from './createFeedbackTicket';
import { ThemeColors } from './useTheme';

interface FeedbackManagementProps {
  colors: ThemeColors;
}

type LoadState = 'loading' | 'loaded' | 'error';

/** Per-row ticket-filing UI state, keyed by turnId. 'closed' means the inline form isn't open for that row. */
type TicketFormState =
  | { step: 'closed' }
  | { step: 'open'; adminNotes: string; error: string | null }
  | { step: 'submitting'; adminNotes: string };

function formatTimestamp(iso: string): string {
  if (!iso) return 'Unknown time';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString();
}

const SINCE_DAYS_LABELS: Record<SinceDays, string> = {
  7: '7d',
  14: '14d',
  30: '30d',
  60: '60d',
};

export function FeedbackManagement({ colors }: FeedbackManagementProps): React.ReactElement {
  const [sinceDays, setSinceDays] = useState<SinceDays>(7);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [items, setItems] = useState<AdminFeedbackItem[]>([]);
  const [skippedCount, setSkippedCount] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ticketForms, setTicketForms] = useState<Record<string, TicketFormState>>({});
  const [toast, setToast] = useState<string | null>(null);

  const loadFeedback = useCallback((days: SinceDays) => {
    setLoadState('loading');
    setLoadError(null);
    // Reset any in-progress ticket-filing form state on every fetch --
    // switching the sinceDays window (or retrying after an error) can
    // change which items are even present; stale per-turnId form state
    // for an item no longer in view would otherwise linger indefinitely
    // (harmless since it's keyed by turnId and simply never renders, but
    // still worth clearing rather than accumulating unbounded state across
    // window switches in a long admin session).
    setTicketForms({});
    fetchAdminFeedback(days)
      .then((result) => {
        setItems(result.feedback);
        setSkippedCount(result.skippedCount);
        setLoadState('loaded');
      })
      .catch((err: unknown) => {
        setLoadError(err instanceof Error ? err.message : 'Unable to load feedback');
        setLoadState('error');
      });
  }, []);

  useEffect(() => {
    loadFeedback(sinceDays);
  }, [sinceDays, loadFeedback]);

  // Auto-dismiss toast, matching FeedbackButton's existing convention.
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  function openTicketForm(turnId: string): void {
    setTicketForms((prev) => ({ ...prev, [turnId]: { step: 'open', adminNotes: '', error: null } }));
  }

  function closeTicketForm(turnId: string): void {
    setTicketForms((prev) => {
      const next = { ...prev };
      delete next[turnId];
      return next;
    });
  }

  function updateAdminNotes(turnId: string, adminNotes: string): void {
    setTicketForms((prev) => {
      const current = prev[turnId];
      if (!current || current.step !== 'open') return prev;
      return { ...prev, [turnId]: { ...current, adminNotes, error: null } };
    });
  }

  async function handleSubmitTicket(item: AdminFeedbackItem): Promise<void> {
    const current = ticketForms[item.turnId];
    if (!current || current.step !== 'open') return;
    const adminNotes = current.adminNotes.trim();

    setTicketForms((prev) => ({ ...prev, [item.turnId]: { step: 'submitting', adminNotes: current.adminNotes } }));

    try {
      const result = await createFeedbackTicket(item.turnId, adminNotes.length > 0 ? adminNotes : undefined);
      setItems((prev) =>
        prev.map((existing) =>
          existing.turnId === item.turnId ? { ...existing, ticketId: result.ticketId } : existing,
        ),
      );
      closeTicketForm(item.turnId);
      setToast(`Ticket created: ${result.ticketId}`);
    } catch (err) {
      if (err instanceof CreateFeedbackTicketError && err.status === 409) {
        const existingTicketId = err.existingTicketId;
        setItems((prev) =>
          prev.map((existing) =>
            existing.turnId === item.turnId ? { ...existing, ticketId: existingTicketId ?? existing.ticketId } : existing,
          ),
        );
        closeTicketForm(item.turnId);
        return;
      }
      const message = err instanceof Error ? err.message : 'Unable to create ticket';
      setTicketForms((prev) => ({ ...prev, [item.turnId]: { step: 'open', adminNotes: current.adminNotes, error: message } }));
    }
  }

  const negativeItems = items.filter((item) => item.rating === 'down');
  const positiveCount = items.filter((item) => item.rating === 'up').length;
  const negativeCount = negativeItems.length;
  const totalCount = items.length;

  return (
    <div style={{ padding: '16px 20px' }}>
      {/* Time-range control */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
        {VALID_SINCE_DAYS.map((days) => (
          <button
            key={days}
            onClick={() => setSinceDays(days)}
            style={{
              padding: '6px 14px',
              border: `1px solid ${sinceDays === days ? colors.suggestionText : colors.border}`,
              borderRadius: '16px',
              background: sinceDays === days ? colors.suggestionBg : 'transparent',
              color: sinceDays === days ? colors.suggestionText : colors.textMuted,
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: sinceDays === days ? 600 : 400,
            }}
          >
            {SINCE_DAYS_LABELS[days]}
          </button>
        ))}
      </div>

      {loadState === 'loading' && <div style={{ color: colors.textMuted, padding: '20px 0' }}>Loading feedback...</div>}

      {loadState === 'error' && (
        <div style={{ padding: '20px 0' }}>
          <div style={{ color: '#f44336', marginBottom: '8px' }}>Unable to load feedback: {loadError}</div>
          <button
            onClick={() => loadFeedback(sinceDays)}
            style={{ padding: '6px 12px', border: `1px solid ${colors.border}`, borderRadius: '4px', background: 'transparent', color: colors.text, cursor: 'pointer', fontSize: '13px' }}
          >
            Retry
          </button>
        </div>
      )}

      {loadState === 'loaded' && (
        <>
          {/* Aggregate metrics, scoped to the selected window */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1, border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '22px', fontWeight: 600 }}>{totalCount}</div>
              <div style={{ fontSize: '12px', color: colors.textMuted }}>Total feedback</div>
            </div>
            <div style={{ flex: 1, border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '22px', fontWeight: 600, color: colors.suggestionText }}>👍 {positiveCount}</div>
              <div style={{ fontSize: '12px', color: colors.textMuted }}>Positive</div>
            </div>
            <div style={{ flex: 1, border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '22px', fontWeight: 600, color: '#f44336' }}>👎 {negativeCount}</div>
              <div style={{ fontSize: '12px', color: colors.textMuted }}>Negative</div>
            </div>
          </div>

          {/* skippedCount is table-wide, not scoped to the sinceDays window (§9.2) --
              wording below states that explicitly so this number isn't misread as
              "N items skipped within this window." */}
          {skippedCount > 0 && (
            <div style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '12px' }}>
              Note: {skippedCount} feedback item{skippedCount === 1 ? '' : 's'} across all time could not be displayed.
            </div>
          )}

          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Negative feedback</div>

          {negativeItems.length === 0 && (
            <div style={{ color: colors.textMuted, padding: '12px 0' }}>No negative feedback in this window.</div>
          )}

          {negativeItems.map((item) => {
            const form = ticketForms[item.turnId] ?? { step: 'closed' as const };
            const hasTurnContent = item.question !== undefined || item.answer !== undefined;
            return (
              <div
                key={item.turnId}
                style={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  padding: '12px',
                  marginBottom: '10px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                  <span style={{ fontSize: '11px', color: colors.textMuted }}>{formatTimestamp(item.createdAt)}</span>
                  {item.ticketId && (
                    <a
                      href={`https://tickety.amazon.dev/tickets/${item.ticketId}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: '12px', color: colors.suggestionText }}
                    >
                      Ticket {item.ticketId} ↗
                    </a>
                  )}
                </div>

                {item.comment && <div style={{ fontSize: '13px', marginBottom: '8px' }}>💬 {item.comment}</div>}

                {hasTurnContent ? (
                  <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                    {item.question !== undefined && (
                      <div style={{ marginBottom: '6px' }}>
                        <span style={{ fontWeight: 600 }}>Q: </span>
                        {item.question}
                      </div>
                    )}
                    {item.answer !== undefined && (
                      <div style={{ marginBottom: '6px' }}>
                        <span style={{ fontWeight: 600 }}>A: </span>
                        {item.answer}
                      </div>
                    )}
                    {item.referencedFiles !== undefined && item.referencedFiles.length > 0 && (
                      <div style={{ fontSize: '11px', color: colors.textMuted }}>
                        Referenced files: {item.referencedFiles.join(', ')}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: colors.textMuted, fontStyle: 'italic', marginBottom: '8px' }}>
                    Original question/answer not available — submitted before this feature shipped.
                  </div>
                )}

                <div style={{ fontSize: '11px', color: colors.textMuted, fontFamily: 'monospace' }}>
                  turn: {item.turnId} · conversation: {item.conversationId}
                </div>

                {!item.ticketId && form.step === 'closed' && (
                  <button
                    onClick={() => openTicketForm(item.turnId)}
                    style={{ marginTop: '8px', padding: '6px 12px', backgroundColor: '#ff8f00', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                  >
                    File Tickety ticket
                  </button>
                )}

                {!item.ticketId && (form.step === 'open' || form.step === 'submitting') && (
                  <div style={{ marginTop: '8px' }}>
                    <textarea
                      value={form.adminNotes}
                      onChange={(e) => updateAdminNotes(item.turnId, e.target.value)}
                      placeholder="Admin notes (optional)..."
                      disabled={form.step === 'submitting'}
                      style={{
                        width: '100%',
                        minHeight: '60px',
                        padding: '8px',
                        border: `1px solid ${form.step === 'open' && form.error ? '#f44336' : colors.border}`,
                        borderRadius: '4px',
                        fontSize: '12px',
                        resize: 'vertical',
                        boxSizing: 'border-box',
                        backgroundColor: 'transparent',
                        color: colors.text,
                      }}
                    />
                    {form.step === 'open' && form.error && (
                      <div style={{ fontSize: '11px', color: '#f44336', marginTop: '4px' }}>{form.error}</div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                      <button
                        onClick={() => closeTicketForm(item.turnId)}
                        disabled={form.step === 'submitting'}
                        style={{ padding: '6px 12px', border: `1px solid ${colors.border}`, borderRadius: '4px', background: 'transparent', color: colors.text, cursor: 'pointer', fontSize: '12px' }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSubmitTicket(item)}
                        disabled={form.step === 'submitting'}
                        style={{ padding: '6px 12px', backgroundColor: '#ff8f00', color: 'white', border: 'none', borderRadius: '4px', cursor: form.step === 'submitting' ? 'default' : 'pointer', fontSize: '12px', opacity: form.step === 'submitting' ? 0.6 : 1 }}
                      >
                        {form.step === 'submitting' ? 'Filing...' : 'File ticket'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#4caf50',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '6px',
            fontSize: '13px',
            zIndex: 2100,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
